
import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

interface AdminSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export default function AdminSidebar({ activeTab, onTabChange }: AdminSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    const supabase = createClient(
      import.meta.env.VITE_PUBLIC_SUPABASE_URL,
      import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
    );
    
    await supabase.auth.signOut();
    window.REACT_APP_NAVIGATE('/admin-login');
  };

  const handleTabClick = (tab: string) => {
    onTabChange(tab);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
            <i className="ri-leaf-line text-base text-white"></i>
          </div>
          <div>
            <h1 className="font-bold text-gray-900 text-sm">Greenify Edu</h1>
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer text-slate-700 hover:bg-gray-100 transition-colors"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/30"
          onClick={() => setMobileOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static top-0 left-0 z-40 h-screen w-64 bg-white border-r border-gray-200 flex flex-col
        transition-transform duration-300
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
              <i className="ri-leaf-line text-xl text-white"></i>
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-sm">Greenify Edu</h1>
              <p className="text-xs text-gray-500">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <button
            onClick={() => handleTabClick('overview')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'overview'
                ? 'bg-teal-50 text-teal-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-dashboard-line text-lg"></i>
            <span className="font-medium text-sm">Overview</span>
          </button>

          <button
            onClick={() => handleTabClick('bookings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'bookings'
                ? 'bg-teal-50 text-teal-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-calendar-check-line text-lg"></i>
            <span className="font-medium text-sm">Bookings</span>
          </button>

          <button
            onClick={() => handleTabClick('availability')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'availability'
                ? 'bg-teal-50 text-teal-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-time-line text-lg"></i>
            <span className="font-medium text-sm">Availability</span>
          </button>

          <button
            onClick={() => handleTabClick('payment')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'payment'
                ? 'bg-teal-50 text-teal-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-bank-card-line text-lg"></i>
            <span className="font-medium text-sm">Payment Settings</span>
          </button>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-line text-lg"></i>
            <span className="font-medium text-sm">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}
